import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Loader2, RefreshCw, ThumbsDown, ThumbsUp } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export const CopyButton = ({ text, label = "Copy", testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setDone(true);
    toast.success("Copied");
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E0D8] bg-white px-3 py-1 text-xs font-semibold hover:border-[#1E3A2B]" data-testid={testId}>
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{label}
    </button>
  );
};

const Screening = ({ doc, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const s = doc.screening;
  const rerun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/submissions/${doc.id}/screen`);
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-[#E5E0D8] p-4 space-y-3" data-testid="candidate-screening">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#B25B41]">AI resume match</h3>
        <button type="button" onClick={rerun} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A2B]" data-testid="candidate-rescreen-button">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}{s ? "Re-run" : "Run analysis"}
        </button>
      </div>
      {!s && <p className="text-sm text-[#5A6561]">Analysis in progress or not yet run. Click Run analysis to refresh.</p>}
      {s?.error && <p className="text-sm text-red-700">{s.error}</p>}
      {s && !s.error && (
        <>
          <p className="font-display text-4xl font-semibold text-[#1E3A2B]" data-testid="candidate-match-percent">{s.match_percent}%<span className="text-sm font-sans font-medium text-[#5A6561] ml-2">estimated match</span></p>
          <p className="text-sm">{s.summary}</p>
          {s.strengths?.length > 0 && <div><p className="text-xs font-semibold text-emerald-800">Matches</p><ul className="list-disc pl-5 text-sm">{s.strengths.map((x) => <li key={x}>{x}</li>)}</ul></div>}
          {s.gaps?.length > 0 && <div><p className="text-xs font-semibold text-amber-800">Missing or unclear</p><ul className="list-disc pl-5 text-sm">{s.gaps.map((x) => <li key={x}>{x}</li>)}</ul></div>}
          {!s.resume_analyzed && <p className="text-xs text-[#5A6561]">No readable resume text; based on application answers only.</p>}
          <p className="text-[11px] text-[#5A6561]">A guide for your review only, not a hiring decision.</p>
        </>
      )}
    </div>
  );
};

const Decision = ({ doc, onUpdated }) => {
  const decide = async (decision) => {
    try {
      const { data } = await api.post(`/admin/submissions/${doc.id}/decision`, { decision });
      onUpdated(data);
      toast.success("Email prepared");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const email = doc.decision_email;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => decide("move_forward")} className={`btn-pill !px-3 !py-2 ${doc.decision === "move_forward" ? "btn-primary" : "btn-outline"}`} data-testid="candidate-yes-button"><ThumbsUp className="h-4 w-4" /> Yes, Move Forward</button>
        <button type="button" onClick={() => decide("do_not_move_forward")} className={`btn-pill !px-3 !py-2 ${doc.decision === "do_not_move_forward" ? "bg-red-700 text-white border border-transparent" : "btn-outline"}`} data-testid="candidate-no-button"><ThumbsDown className="h-4 w-4" /> No, Do Not Move Forward</button>
      </div>
      {email && (
        <div className="rounded-2xl bg-[#FAF8F5] border border-[#E5E0D8] p-4 space-y-3" data-testid="candidate-email">
          <div className="flex flex-wrap items-center gap-2 text-sm"><span className="text-[#5A6561]">To:</span><span className="font-semibold">{email.to}</span><CopyButton text={email.to} label="Copy email" testId="candidate-copy-to" /></div>
          <div className="flex flex-wrap items-center gap-2 text-sm"><span className="text-[#5A6561]">Subject:</span><span className="font-semibold">{email.subject}</span><CopyButton text={email.subject} label="Copy subject" testId="candidate-copy-subject" /></div>
          <pre className="whitespace-pre-wrap font-sans text-sm bg-white rounded-xl border border-[#E5E0D8] p-3" data-testid="candidate-email-body">{email.body}</pre>
          <CopyButton text={email.body} label="Copy message" testId="candidate-copy-body" />
        </div>
      )}
    </div>
  );
};

export const CandidatePanel = ({ doc, onUpdated }) => (
  <div className="space-y-4" data-testid="candidate-panel">
    <Screening doc={doc} onUpdated={onUpdated} />
    <Decision doc={doc} onUpdated={onUpdated} />
  </div>
);
